import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { api } from '@/utils/api';
import { useAuth } from '@/contexts/AuthContext';

export interface FavoriteProduct {
  id: string;
  name: string;
  basePrice: number;
  description: string;
  category: { id: string; name: string; slug: string };
  media: { id: string; type: string; url: string; publicId: string; order: number }[];
  variants: Array<{ id: string; name: string; sku: string; price: number | null; stock: number; attributes: Record<string, string> }>;
  status: string;
  minOrderQuantity: number;
  isFavorite: boolean;
}

interface FavorisContextValue {
  favorites: FavoriteProduct[];
  loading: boolean;
  error: string | null;
  fetchFavorites: () => Promise<FavoriteProduct[]>;
  toggleFavorite: (productId: string) => Promise<boolean>;
  isFavorite: (productId: string) => boolean;
  clearError: () => void;
}

const FavorisContext = createContext<FavorisContextValue | null>(null);

export function useFavoris(): FavorisContextValue {
  const ctx = useContext(FavorisContext);
  if (!ctx) throw new Error('useFavoris must be used within FavorisProvider');
  return ctx;
}

export function FavorisProvider({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated } = useAuth();
  const [favorites, setFavorites] = useState<FavoriteProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => setError(null), []);

  const handleError = useCallback((err: unknown, fallback: string) => {
    const message = err instanceof Error ? err.message : fallback;
    setError(message);
    return null;
  }, []);

  const fetchFavorites = useCallback(async (): Promise<FavoriteProduct[]> => {
    if (!isAuthenticated || !user?.id) {
      setFavorites([]);
      return [];
    }

    setLoading(true);
    setError(null);
    try {
      const data = await api.get<FavoriteProduct[]>('/products/favorites/list');
      const list = Array.isArray(data) ? data : [];
      setFavorites(list);
      return list;
    } catch (err) {
      handleError(err, 'Impossible de charger les favoris.');
      return [];
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user?.id, handleError]);

  const toggleFavorite = useCallback(async (productId: string): Promise<boolean> => {
    if (!isAuthenticated || !user?.id) {
      setError('Vous devez être connecté pour ajouter des favoris.');
      return false;
    }

    setError(null);
    try {
      const result = await api.post<{ favorited: boolean }>(`/products/${productId}/favorite`, {});
      const newFavorited = result?.favorited || false;

      if (newFavorited) {
        await fetchFavorites();
      } else {
        setFavorites((prev) => prev.filter((f) => f.id !== productId));
      }

      return newFavorited;
    } catch (err) {
      handleError(err, 'Impossible de modifier les favoris.');
      return false;
    }
  }, [isAuthenticated, user?.id, handleError, fetchFavorites]);

  const isFavorite = useCallback((productId: string): boolean => {
    return favorites.some((f) => f.id === productId);
  }, [favorites]);

  useEffect(() => {
    if (isAuthenticated && user?.id) {
      fetchFavorites();
    } else {
      setFavorites([]);
    }
  }, [isAuthenticated, user?.id, fetchFavorites]);

  const value: FavorisContextValue = {
    favorites,
    loading,
    error,
    fetchFavorites,
    toggleFavorite,
    isFavorite,
    clearError,
  };

  return <FavorisContext.Provider value={value}>{children}</FavorisContext.Provider>;
}
